import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('ProfileImageService', 'storage');

interface UploadResult {
  url: string;
  path: string;
}

interface ImageDimensions {
  width: number;
  height: number;
}

/**
 * Service for handling profile image uploads with optimization and storage management
 */
class ProfileImageService {
  private readonly MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  private readonly ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
  private readonly DIMENSIONS = {
    profile: { width: 400, height: 400 },
    signature: { width: 600, height: 200 },
    thumbnail: { width: 100, height: 100 }
  };

  /**
   * Compress and resize image before upload
   */
  private async optimizeImage(
    file: File, 
    maxWidth: number, 
    maxHeight: number,
    quality: number = 0.85
  ): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      // Create object URL for the image first
      const imageUrl = URL.createObjectURL(file);
      
      img.onload = () => {
        // Clean up the object URL
        URL.revokeObjectURL(imageUrl);
        
        // Calculate new dimensions maintaining aspect ratio
        let { width, height } = img;
        
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }

        // Set canvas dimensions
        canvas.width = width;
        canvas.height = height;

        // Apply white background for JPEGs (in case of transparency)
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, width, height);

        // Draw the image
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to blob
        canvas.toBlob(
          (blob) => {
            if (blob) {
              log.debug('Image optimized', {
                originalSize: file.size,
                optimizedSize: blob.size,
                reduction: `${Math.round((1 - blob.size / file.size) * 100)}%`
              });
              resolve(blob);
            } else {
              reject(new Error('Failed to compress image'));
            }
          },
          'image/jpeg',
          quality
        );
      };

      img.onerror = () => {
        URL.revokeObjectURL(imageUrl);
        reject(new Error('Failed to load image'));
      };
      
      img.src = imageUrl;
    });
  }

  /**
   * Validate image file before upload
   */
  private validateFile(file: File): void {
    if (!file) {
      throw new Error('No file provided');
    }

    if (!this.ALLOWED_TYPES.includes(file.type)) {
      throw new Error(`Invalid file type. Allowed types: ${this.ALLOWED_TYPES.join(', ')}`);
    }

    if (file.size > this.MAX_FILE_SIZE * 1.5) { // Allow 1.5x size before optimization (7.5MB)
      throw new Error(`File too large. Maximum size: ${this.MAX_FILE_SIZE / 1024 / 1024}MB after optimization`);
    }
  }

  /**
   * Upload profile image with fixed filename (replaces existing)
   */
  async uploadProfileImage(
    userId: string,
    file: File,
    type: 'profile' | 'signature' = 'profile'
  ): Promise<UploadResult> {
    try {
      // Validate input
      this.validateFile(file);

      // Get dimensions for this image type
      const dimensions = this.DIMENSIONS[type];
      
      // Optimize the image
      let optimizedBlob = await this.optimizeImage(
        file, 
        dimensions.width, 
        dimensions.height,
        0.85
      );

      // Check optimized size
      if (optimizedBlob.size > this.MAX_FILE_SIZE) {
        // Try again with lower quality
        const reoptimizedBlob = await this.optimizeImage(
          file,
          dimensions.width,
          dimensions.height,
          0.70
        );
        
        if (reoptimizedBlob.size > this.MAX_FILE_SIZE) {
          throw new Error('Image too large even after optimization. Please use a smaller image.');
        }
        
        optimizedBlob = reoptimizedBlob;
      }

      // Use fixed filename for replacement (prevents accumulation)
      const fileName = `${userId}/${type}.jpg`;

      log.info('Uploading image', { userId, type, fileName, size: optimizedBlob.size });

      // Upload to Supabase (upsert replaces existing)
      const { data, error } = await supabase.storage
        .from('profile-images')
        .upload(fileName, optimizedBlob, {
          contentType: 'image/jpeg',
          cacheControl: '3600',
          upsert: true // Replace existing file
        });

      if (error) {
        log.error('Upload failed', { userId, type, error: error.message }, error);
        throw new Error(`Failed to upload image: ${error.message}`);
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('profile-images')
        .getPublicUrl(data.path);
      
      // Add timestamp to bust cache on frontend
      const urlWithCacheBuster = `${publicUrl}?t=${Date.now()}`;

      log.info('Image uploaded successfully', { 
        userId, 
        type, 
        path: data.path, 
        url: urlWithCacheBuster 
      });

      // Update the database with the new URL
      await this.updateDatabaseUrl(userId, type, urlWithCacheBuster);

      return {
        url: urlWithCacheBuster,
        path: data.path
      };
    } catch (error) {
      log.error('Error in uploadProfileImage', { userId, type }, error as Error);
      throw error;
    }
  }

  /**
   * Update database with new image URL
   */
  private async updateDatabaseUrl(
    userId: string, 
    type: 'profile' | 'signature', 
    url: string
  ): Promise<void> {
    const column = type === 'profile' ? 'profile_photo' : 'signature_image';
    
    const { error } = await supabase
      .from('users')
      .update({ [column]: url })
      .eq('user_id', userId);

    if (error) {
      log.error('Failed to update database', { userId, type, column }, error);
      // Don't throw - image is uploaded, just DB update failed
      // The calling code can handle this by reloading data
    }
  }

  /**
   * Get profile image URL for any user (cross-system access)
   */
  async getProfileImageUrl(
    userId: string, 
    type: 'profile' | 'signature' = 'profile'
  ): Promise<string | null> {
    try {
      // First check database for stored URL
      const column = type === 'profile' ? 'profile_photo' : 'signature_image';
      const { data, error } = await supabase
        .from('users')
        .select(column)
        .eq('user_id', userId)
        .single();

      if (!error && data && data[column]) {
        return data[column];
      }

      // Fallback: Check if file exists in storage
      const path = `${userId}/${type}.jpg`;
      const { data: files } = await supabase.storage
        .from('profile-images')
        .list(userId, { limit: 10 });

      if (files?.find(f => f.name === `${type}.jpg`)) {
        const { data: { publicUrl } } = supabase.storage
          .from('profile-images')
          .getPublicUrl(path);
        
        return `${publicUrl}?t=${Date.now()}`;
      }

      return null;
    } catch (error) {
      log.error('Error getting profile image URL', { userId, type }, error as Error);
      return null;
    }
  }

  /**
   * Delete profile image
   */
  async deleteProfileImage(
    userId: string, 
    type: 'profile' | 'signature' = 'profile'
  ): Promise<void> {
    try {
      const path = `${userId}/${type}.jpg`;
      
      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('profile-images')
        .remove([path]);

      if (storageError) {
        log.error('Failed to delete from storage', { userId, type, path }, storageError);
      }

      // Clear from database
      const column = type === 'profile' ? 'profile_photo' : 'signature_image';
      const { error: dbError } = await supabase
        .from('users')
        .update({ [column]: null })
        .eq('user_id', userId);

      if (dbError) {
        log.error('Failed to clear database', { userId, type, column }, dbError);
      }

      if (storageError || dbError) {
        throw new Error('Failed to delete image completely');
      }

      log.info('Image deleted', { userId, type });
    } catch (error) {
      log.error('Error in deleteProfileImage', { userId, type }, error as Error);
      throw error;
    }
  }

  /**
   * Check if user has a profile image
   */
  async hasProfileImage(userId: string, type: 'profile' | 'signature' = 'profile'): Promise<boolean> {
    const url = await this.getProfileImageUrl(userId, type);
    return url !== null;
  }
}

// Export singleton instance
export const profileImageService = new ProfileImageService();